import { searchRootQuery } from "./hooks/native_search.mjs"

export const MOBILE_WEBVIEW_NAVIGATION_EVENT = "enaia:mobile:navigate:v1"

const SCHEMA_VERSION = 1
const MAX_DETAIL_BYTES = 96
const DETAIL_KEYS = ["destination", "schema_version"]
const DESTINATION_PATHS = Object.freeze({
  accounts: "/accounts",
  prospects: "/tracker",
  deals: "/pipeline",
  clients: "/clients",
  teams: "/teams",
  search: "/search",
})

function hasExactKeys(detail) {
  const keys = Object.keys(detail).sort()

  return (
    keys.length === DETAIL_KEYS.length &&
    keys.every((key, index) => key === DETAIL_KEYS[index])
  )
}

function pathForDetail(detail) {
  if (!detail || Array.isArray(detail) || typeof detail !== "object")
    return null
  if (!hasExactKeys(detail)) return null
  if (
    detail.schema_version !== SCHEMA_VERSION ||
    typeof detail.destination !== "string"
  ) {
    return null
  }

  const encodedDetail = new TextEncoder().encode(JSON.stringify(detail))
  if (encodedDetail.byteLength > MAX_DETAIL_BYTES) return null
  if (
    !Object.prototype.hasOwnProperty.call(DESTINATION_PATHS, detail.destination)
  )
    return null

  return DESTINATION_PATHS[detail.destination]
}

// Search retirement and dispatch must agree before either changes state.
export function acceptedMobileNavigation(event, liveSocket) {
  const path = pathForDetail(event.detail)
  if (!path || event.cancelable !== true) return null
  if (
    !liveSocket ||
    typeof liveSocket.isConnected !== "function" ||
    !liveSocket.isConnected() ||
    typeof liveSocket.js !== "function" ||
    typeof event.preventDefault !== "function"
  )
    return null

  const js = liveSocket.js()
  if (!js || typeof js.navigate !== "function") return null
  return { path, js }
}

export function createMobileWebViewNavigationHandler(liveSocket) {
  return (event) => {
    const navigation = acceptedMobileNavigation(event, liveSocket)
    if (!navigation) return
    const { path, js } = navigation

    // Cancellation is the synchronous acknowledgement native uses to skip its
    // same-origin top-level fallback.
    event.preventDefault()
    // Only a Search root the transport accepts is safe to keep; fragments and
    // malformed queries leave the capability unavailable, so recover them.
    if (
      path === "/search" &&
      globalThis.location &&
      searchRootQuery(globalThis.location) !== null
    )
      return
    js.navigate(path)
  }
}

/**
 * Installs the document-lifetime `enaia:mobile:navigate:v1` window event.
 *
 * Native may dispatch only a cancelable CustomEvent with exact detail
 * `{schema_version: 1, destination: "accounts" | "prospects" | "deals" |
 * "clients" | "teams" | "search"}`. The closed enum maps to routes already available to
 * the authenticated user; no URL or native capability crosses this boundary.
 */
export function initializeMobileWebViewNavigation(liveSocket) {
  window.addEventListener(
    MOBILE_WEBVIEW_NAVIGATION_EVENT,
    createMobileWebViewNavigationHandler(liveSocket),
  )
}
